/**
 * Shell side of the omni/1 bridge. Every call an app makes goes through
 * here and is executed with that app's short-lived, scoped token (ADR-0005).
 */
import { PROTOCOL, type ApiRequestParams, type CameraOpenParams, type CameraRect, type InitResult, type MethodName, type Methods, type NavigateTarget, type OmniEvent, type Theme } from "@omni/sdk/protocol";

import { apiBase, errorFrom, withQuery } from "./api";
import { captureFlush, captureSet, captureSettings, captureStatus, currentPosition, isSettingsTarget } from "./capture";
import { load, save } from "./storage";

export interface AppSession {
  token: string;
  expires_in: number;
  frontend_url: string;
  api_base: string;
  app: InitResult["app"] & { fullscreen: boolean };
  plan_id: string;
  scopes: string[];
  user: InitResult["user"];
  /** ADR-0024: the bundle version this session is for */
  bundle?: { version: string; hash: string; base_url: string; entry: string } | null;
  /** ADR-0025: event patterns this app may hear */
  events?: string[];
  /** Permissions a newer version asks for that the person hasn't granted yet */
  pending_permissions?: { permission: string; description: string }[];
}

export interface BridgeHost {
  appId: string;
  /** Resolves once the session is known: the app may start before it is. */
  getSession: () => Promise<AppSession>;
  renewSession: () => Promise<AppSession>;
  theme: () => Theme;
  route: () => string;
  platform: InitResult["platform"];
  toast: (message: string, kind?: "info" | "success" | "error") => void;
  setTitle: (title: string) => void;
  navigate: (target: NavigateTarget) => void;
  setRoute: (path: string) => void;
  openExternal: (url: string) => Promise<void>;
  share: (data: { title?: string; text?: string; url?: string }) => Promise<boolean>;
  /** Ask the person for a permission the app declares but wasn't granted (resolves true if allowed). */
  askPermission: (permission: string) => Promise<boolean>;
  /** The live camera the shell draws over the app (apps can't open one themselves). */
  camera: {
    open: (p: CameraOpenParams) => Promise<{ width: number; height: number }>;
    move: (p: { rect?: CameraRect; guide?: CameraOpenParams["guide"] }) => void;
    capture: () => Promise<Blob>;
    close: () => void;
  };
}

export class BridgeError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
  }
}

function allowedPath(appId: string, path: string): boolean {
  if (path.includes("..") || !path.startsWith("/")) return false;
  const clean = path.split("?")[0];
  return (
    clean === `/api/m/${appId}` ||
    clean.startsWith(`/api/m/${appId}/`) ||
    clean.startsWith("/api/data/") ||
    clean === "/api/ai/complete" ||
    clean === "/api/ai/budget" ||
    clean === "/api/files" ||
    clean.startsWith("/api/files/")
  );
}

async function apiRequest(host: BridgeHost, p: ApiRequestParams, internal = false): Promise<unknown> {
  if (!internal && !allowedPath(host.appId, p.path)) throw new BridgeError(`Path not allowed for apps: ${p.path}`, 403);
  const send = (session: AppSession) => {
    const headers: Record<string, string> = { Authorization: `Bearer ${session.token}` };
    let body: BodyInit | undefined;
    if (p.body !== undefined && p.body !== null) {
      if (p.bodyType === "form") {
        const form = new FormData();
        for (const [name, value, filename] of p.body as [string, string | Blob, string?][]) {
          if (value instanceof Blob) form.append(name, value, filename);
          else form.append(name, value);
        }
        body = form;
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(p.body);
      }
    }
    return fetch(`${apiBase()}${withQuery(p.path, p.query)}`, { method: p.method ?? "GET", headers, body });
  };
  let res = await send(await host.getSession());
  if (res.status === 401) res = await send(await host.renewSession());
  if (!res.ok) {
    const err = await errorFrom(res);
    throw new BridgeError(err.message, err.status);
  }
  if (res.status === 204) return null;
  if (p.responseType === "blob") return await res.blob();
  if (p.responseType === "text") return await res.text();
  return await res.json();
}

type Handler<M extends MethodName> = (host: BridgeHost, params: Methods[M]["params"]) => Promise<Methods[M]["result"]> | Methods[M]["result"];

const handlers: { [M in MethodName]: Handler<M> } = {
  init: async (host) => {
    const s = await host.getSession();
    return {
      app: s.app,
      user: s.user,
      plan: s.plan_id,
      scopes: s.scopes,
      theme: host.theme(),
      platform: host.platform,
      route: host.route(),
    };
  },
  "api.request": (host, p) => apiRequest(host, p),
  "events.publish": (host, p) =>
    apiRequest(host, { path: "/api/events", method: "POST", body: { type: String(p.type), data: p.data ?? {} } }, true) as Promise<OmniEvent>,
  "ui.toast": (host, p) => host.toast(String(p.message).slice(0, 300), p.kind),
  "ui.setTitle": (host, p) => host.setTitle(String(p.title).slice(0, 60)),
  "ui.navigate": (host, p) => host.navigate(p),
  "ui.openExternal": async (host, p) => {
    if (!/^https?:\/\//.test(p.url) && !/^tel:\+?[0-9 ()*#.-]{1,40}$/.test(p.url)) throw new BridgeError("Only http(s) and tel: links can be opened", 400);
    await host.openExternal(p.url);
  },
  "ui.route": (host, p) => host.setRoute(p.path),
  share: async (host, p) => ({ shared: await host.share(p) }),
  "camera.open": async (host, p) => {
    const s = await host.getSession();
    // Asked when it's needed, like the phone does, not in a banner that can be missed
    if (!s.scopes.includes("camera") && !(await host.askPermission("camera")))
      throw new BridgeError("Camera access for this app is off", 403);
    return host.camera.open(p);
  },
  "capture.status": async (host) => captureStatus(host.appId, (await host.getSession()).scopes),
  "capture.set": async (host, p) => {
    if (!["calls", "notifications", "location"].includes(p.source)) throw new BridgeError("Unknown source", 400);
    let s = await host.getSession();
    const perm = `device:${p.source}`;
    if (p.enabled && !s.scopes.includes(perm)) {
      if (!(await host.askPermission(perm))) throw new BridgeError("Recording this is off for this app", 403);
      s = await host.renewSession(); // the new token carries the permission
    }
    return captureSet(host.appId, s.scopes, p.source, p.enabled);
  },
  "capture.flush": async (host) => captureFlush(host.appId, (await host.getSession()).scopes),
  "capture.settings": async (host, p) => {
    if (!isSettingsTarget(p.source)) throw new BridgeError("Unknown settings", 400);
    return captureSettings(p.source);
  },
  "location.current": async (host) => {
    const s = await host.getSession();
    if (!s.scopes.includes("device:location") && !(await host.askPermission("device:location")))
      throw new BridgeError("Location for this app is off", 403);
    try {
      return await currentPosition();
    } catch (e) {
      throw new BridgeError((e as Error).message || "Location unavailable", 404);
    }
  },
  "camera.move": (host, p) => host.camera.move(p),
  "camera.capture": (host) => host.camera.capture(),
  "camera.close": (host) => host.camera.close(),
  "storage.get": (host, p) => load<Record<string, unknown>>(`omni.appstore.${host.appId}`, {})[p.key] ?? null,
  "storage.set": (host, p) => {
    const key = `omni.appstore.${host.appId}`;
    const store = load<Record<string, unknown>>(key, {});
    store[p.key] = p.value;
    if (JSON.stringify(store).length > 256 * 1024) throw new BridgeError("App storage quota (256 KB) exceeded", 413);
    save(key, store);
  },
};

/** Attach the bridge to one iframe. Returns a disposer and an event emitter. */
export function attachBridge(iframe: HTMLIFrameElement, host: BridgeHost) {
  const reply = (id: number, ok: boolean, result?: unknown, error?: { message: string; status?: number }) =>
    iframe.contentWindow?.postMessage({ protocol: PROTOCOL, kind: "response", id, ok, result, error }, "*");

  const onMessage = async (event: MessageEvent) => {
    // Sandboxed frames have an opaque origin; identify the app by its window.
    if (event.source !== iframe.contentWindow) return;
    const msg = event.data;
    if (!msg || msg.protocol !== PROTOCOL || msg.kind !== "request") return;
    const handler = handlers[msg.method as MethodName] as Handler<MethodName> | undefined;
    if (!handler) return reply(msg.id, false, undefined, { message: `Unknown method ${msg.method}`, status: 404 });
    try {
      reply(msg.id, true, await handler(host, msg.params));
    } catch (e) {
      const err = e as BridgeError;
      reply(msg.id, false, undefined, { message: err.message ?? "Error", status: err.status });
    }
  };
  window.addEventListener("message", onMessage);
  return {
    dispose: () => window.removeEventListener("message", onMessage),
    emit: (event: "theme" | "visibility" | "route" | "bus", data: unknown) =>
      iframe.contentWindow?.postMessage({ protocol: PROTOCOL, kind: "event", event, data }, "*"),
  };
}
